<template>
  <section class="adjudication-ways">
    <div class="adjudication-ways__header">
      <DsText as="bebas4" color="var(--ds-purple-900)" class="adjudication-ways__title">
        Formas de adjudicaci&oacute;n
      </DsText>

      <DsText as="body4" class="adjudication-ways__intro">
        Existen diferentes modalidades de adjudicaci&oacute;n dise&ntilde;adas para adaptarse al avance de cada cliente.
        Conoce c&oacute;mo puedes acercarte a la entrega de tu patrimonio seg&uacute;n tu historial y progreso dentro del financiamiento.
      </DsText>
    </div>

    <div class="adjudication-ways__list">
      <article
        v-for="item in adjudicationWays"
        :key="item.title"
        class="adjudication-ways__card"
      >
        <div class="adjudication-ways__card-copy">
          <DsText as="bebas4" color="var(--ds-purple-900)" class="adjudication-ways__card-title">
            {{ item.title }}
          </DsText>
          <DsText as="subtitle7" color="var(--ds-text-primary)" class="adjudication-ways__card-subtitle">
            {{ item.subtitle }}
          </DsText>
          <DsText as="body4" class="adjudication-ways__card-text">
            {{ item.text }}
          </DsText>
        </div>

        <picture class="adjudication-ways__bar" aria-hidden="true">
          <source media="(max-width: 767px)" :srcset="adjudicacionBarMobile" />
          <source media="(max-width: 1023px)" :srcset="adjudicacionBarTablet" />
          <img :src="adjudicacionBarDesktop" alt="" class="adjudication-ways__bar-image" />
        </picture>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import DsText from '../atoms/DsText.vue'

const adjudicacionBarDesktop = new URL('../../../../../assets/adjudicacion/adjudicaciones_bar_desktop.png', import.meta.url).href
const adjudicacionBarMobile = new URL('../../../../../assets/adjudicacion/adjudicaciones_bar_mobile.png', import.meta.url).href
const adjudicacionBarTablet = new URL('../../../../../assets/adjudicacion/adjudicaciones_bar_tablet.png', import.meta.url).href

const adjudicationWays = [
  {
    title: 'Natural',
    subtitle:"Tu constancia habla por ti.",
    text: 'Mientras más pagos mantengas al corriente, más cerca estás de recibir tu patrimonio. Y si llega un empate, tu antigüedad puede marcar la diferencia.'
  },
  {
    title: 'Lista de espera',
    subtitle:"Tu avance también abre oportunidades.",
    text: 'A partir de 54 cuotas pagadas, puedes integrarte a la lista de espera y acercarte antes a la meta de tener tu hogar.'
  },
  {
    title: 'Obligatoria',
    subtitle:"Tu compromiso acelera el camino.",
    text: 'Con 60 cuotas pagadas o anticipadas, y manteniendo puntualidad en tus pagos, aumentan tus posibilidades de adjudicación.'
  },
  {
    title: 'Directa',
    subtitle:"Hay momentos donde la tranquilidad importa más que nunca. ",
    text: 'En casos de fallecimiento o invalidez, la adjudicación puede aplicarse al titular o sus beneficiarios.'
  },
  {
    title: 'Por liquidación',
    subtitle:"Cuando decides dar el último paso, todo cambia. ",
    text: 'Al completar el pago total de tu financiamiento, el proceso de adjudicación se realiza de forma inmediata.'
  }
]
</script>

<style scoped>
.adjudication-ways {
  width: 100%;
  max-width: 962px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.adjudication-ways__header {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.adjudication-ways__title {
  font-size: var(--ds-h4-size);
}

.adjudication-ways__intro {
  color: var(--ds-text-primary);
  font-size: var(--ds-body3-size);
}

/* Figma: rejilla de 3x2 en desktop (tarjetas de 294px). */
.adjudication-ways__list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
}

/* 744px: ancho del diseño de tablet. */
@media (min-width: 744px) {
  .adjudication-ways__list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
  }
}

@media (min-width: 1024px) {
  .adjudication-ways__list {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.adjudication-ways__card {
  position: relative;
  overflow: hidden;
  min-height: 112px;
  padding: 26px 24px 30px;
  background: var(--ds-grey-50);
  border-radius: 24px;
  box-sizing: border-box;
}

.adjudication-ways__card-copy {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
}

.adjudication-ways__card-title {
  font-size: var(--ds-h4-size);
  padding-bottom: 16px;
}

.adjudication-ways__card-subtitle{
  font-size: var(--ds-body3-size);
  font-family: var(--ds-font-family-inter);
}

.adjudication-ways__card-text {
  color: var(--ds-text-primary);
  font-size: var(--ds-body3-size);
  font-family: var(--ds-font-family-inter);
}

.adjudication-ways__bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: block;
  height: 24px;
}

.adjudication-ways__bar-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: left bottom;
}

@media (max-width: 767px) {
  .adjudication-ways {
    max-width: 100%;
    gap: 24px;
  }

  .adjudication-ways__header {
    gap: 18px;
  }

  .adjudication-ways__list {
    gap: 28px;
  }

  .adjudication-ways__card {
    min-height: 144px;
    padding: 20px 18px 30px;
    border-radius: 18px;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .adjudication-ways {
    gap: 28px;
  }

  .adjudication-ways__list {
    gap: 30px;
  }

  .adjudication-ways__card {
    min-height: 120px;
    padding: 24px 22px 32px;
  }
}
</style>
